import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Search, RefreshCw, X, Trash2, AlertTriangle, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

const STATUSES = ["new", "reviewing", "contacted", "interview_scheduled", "interviewed", "hold", "offer_extended", "hired", "not_selected", "withdrawn", "closed"];
const APP_TYPES = [{ value: "job", label: "Job-specific" }, { value: "general", label: "General" }];
const PREFERENCE_OPTIONS = [
  { value: "full_time", label: "Full-Time" }, { value: "part_time", label: "Part-Time" },
  { value: "per_diem", label: "Per Diem" }, { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" }, { value: "internship", label: "Internship" },
  { value: "other", label: "Other" },
];
const prefLabel = (v) => PREFERENCE_OPTIONS.find((o) => o.value === v)?.label || humanize(v);

export default function AdminApplications() {
  const { refreshOverview } = useOutletContext();
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [prefFilter, setPrefFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (jobFilter) params.job_id = jobFilter;
      if (typeFilter) params.application_type = typeFilter;
      if (prefFilter) params.employment_preference = prefFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/applications", { params });
      setApps(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [statusFilter, jobFilter, typeFilter, prefFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/jobs", { params: { include_archived: true } })
      .then((r) => setJobs(r.data))
      .catch(() => {});
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/admin/applications/${id}/status`, { status });
      setApps((p) => p.map((x) => (x.id === id ? r.data : x)));
      if (selected?.id === id) setSelected(r.data);
      toast.success("Status updated");
      refreshOverview?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const addNote = async () => {
    if (!noteDraft.trim() || !selected) return;
    try {
      const r = await api.patch(`/admin/applications/${selected.id}/notes`, { note: noteDraft.trim() });
      setSelected(r.data);
      setApps((p) => p.map((x) => (x.id === r.data.id ? r.data : x)));
      setNoteDraft("");
      toast.success("Note added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const downloadResume = async (app) => {
    try {
      const resp = await api.get(`/admin/applications/${app.id}/resume`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || app.resume_content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = app.resume_filename || `resume-${app.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/applications/${selected.id}`);
      setApps((p) => p.filter((x) => x.id !== selected.id));
      setConfirmDelete(false);
      setSelected(null);
      toast.success("Application deleted");
      refreshOverview?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Applications</h2>
          <p className="mt-1 text-sm text-slate-500">{apps.length} record{apps.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={load} data-testid="refresh-apps" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] self-start"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, employer…" data-testid="search-apps" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-app-status" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
        <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} data-testid="filter-app-job" className="rounded-lg border border-slate-300 px-3 py-2 text-sm max-w-[240px]">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="filter-app-type" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All types</option>
          {APP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={prefFilter} onChange={(e) => setPrefFilter(e.target.value)} data-testid="filter-app-pref" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">Any preference</option>
          {PREFERENCE_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-12 text-center text-slate-500 text-sm">Loading…</div>
        : apps.length === 0 ? <div className="p-12 text-center text-slate-500 text-sm" data-testid="empty-apps">No applications yet.</div>
        : (<div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                <th className="text-left px-4 py-3 font-semibold">Applicant</th>
                <th className="text-left px-4 py-3 font-semibold">Job</th>
                <th className="text-left px-4 py-3 font-semibold">Location</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apps.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50/60" data-testid={`app-row-${a.id}`}>
                  <td className="px-4 py-3 text-slate-500 text-xs align-top">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-900 align-top">
                    <div className="font-medium">{a.first_name} {a.last_name}</div>
                    <div className="text-xs text-slate-500">{a.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 align-top">
                    {a.application_type === "general" ? (
                      <>
                        <div className="font-medium text-slate-900">General Employment Application</div>
                        {(a.employment_preferences || []).length > 0 && (
                          <div className="text-xs text-slate-500 mt-0.5">{(a.employment_preferences || []).map(prefLabel).join(", ")}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div>{a.job_title}</div>
                        {(a.employment_preferences || []).length > 0 && (
                          <div className="text-xs text-slate-500 mt-0.5">Also open to: {(a.employment_preferences || []).map(prefLabel).join(", ")}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 align-top">{a.city}, {a.state}</td>
                  <td className="px-4 py-3 align-top"><span className="inline-flex px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">{humanize(a.status)}</span></td>
                  <td className="px-4 py-3 text-right align-top">
                    <button onClick={() => setSelected(a)} data-testid={`view-app-${a.id}`} className="text-[#4A6FA5] hover:text-[#3B597B] text-sm font-medium">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>)}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="app-drawer">
          <div className="flex-1 bg-slate-900/30" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-white border-l border-slate-200 overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Application</p>
                <h3 className="font-heading text-xl text-slate-900">{selected.first_name} {selected.last_name}</h3>
                <p className="mt-1 text-sm text-slate-600">{selected.job_title}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900" data-testid="close-app-drawer"><X className="w-4 h-4" /></button>
            </div>

            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Status</label>
              <select value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)} data-testid="app-drawer-status" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </select>
            </div>

            <button onClick={() => downloadResume(selected)} data-testid="download-resume" className="w-full mb-4 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">
              <Download className="w-4 h-4" /> Download résumé ({selected.resume_filename || "file"})
            </button>

            <dl className="space-y-3 text-sm">
              <Row label="Email" value={selected.email} />
              <Row label="Phone" value={selected.phone} />
              <Row label="Location" value={`${selected.city}, ${selected.state}`} />
              <Row label="Current employer" value={selected.current_employer} />
              <Row label="Current title" value={selected.current_title} />
              <Row label="LinkedIn" value={selected.linkedin} link />
              <Row label="Website" value={selected.website} link />
              <Row label="Referral source" value={selected.referral_source} />
              {(selected.employment_preferences || []).length > 0 && (
                <Row label="Employment preferences" value={(selected.employment_preferences || []).map(prefLabel).join(", ")} />
              )}
              {selected.employment_preferences_other && <Row label="Other preference note" value={selected.employment_preferences_other} />}
              <Row label="Cover letter" value={selected.cover_letter} pre />
              <Row label="Additional comments" value={selected.additional_comments} pre />
              <Row label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
            </dl>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Internal notes</p>
              <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                {(selected.notes || []).length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
                {(selected.notes || []).map((n) => (
                  <div key={n.id} className="rounded-lg bg-[#F8FAFC] border border-slate-200 p-3 text-sm">
                    <p className="text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={3} data-testid="app-note-input" placeholder="Add an internal note (not visible to applicant)…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
              <button onClick={addNote} disabled={!noteDraft.trim()} data-testid="app-note-save" className="mt-2 inline-flex items-center px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] disabled:opacity-50">Save note</button>
            </div>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-red-700 font-semibold mb-2">Danger zone</p>
              <p className="text-xs text-slate-500 mb-2">Use only for spam, test, duplicate, or clearly invalid submissions. The résumé file will also be removed.</p>
              <button onClick={() => setConfirmDelete(true)} data-testid="delete-app-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50"><Trash2 className="w-4 h-4" /> Delete application</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid="app-delete-modal" onClick={() => !deleting && setConfirmDelete(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-600 shrink-0"><AlertTriangle className="w-5 h-5" /></span>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">Delete this application permanently?</h3>
                <p className="mt-1 text-sm text-slate-600">This will also mark the résumé file for permanent removal. Use only for spam/test/invalid records.</p>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm mb-6">
              <div><span className="text-slate-500">Applicant: </span><span className="text-slate-900 font-medium">{selected.first_name} {selected.last_name}</span></div>
              <div><span className="text-slate-500">Job: </span><span className="text-slate-900">{selected.job_title}</span></div>
              <div><span className="text-slate-500">Email: </span><span className="text-slate-900">{selected.email}</span></div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} disabled={deleting} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={doDelete} disabled={deleting} data-testid="confirm-delete-app" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2">
                {deleting ? "Deleting…" : (<><Trash2 className="w-4 h-4" /> Delete permanently</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, link, pre }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</dt>
      <dd className={`text-slate-800 mt-0.5 ${pre ? "whitespace-pre-wrap" : ""}`}>
        {link ? <a href={value} target="_blank" rel="noreferrer" className="text-[#4A6FA5] hover:text-[#3B597B] break-all">{value}</a> : value}
      </dd>
    </div>
  );
}
